import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {{id: number, available?: boolean}} SizeVariantMapEntry
 * @typedef {Record<string, SizeVariantMapEntry>} SizeVariantMap
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {TextComponent} [cartTotalSaving] - The cart total saving amount.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Sync immediately and again after layout/fonts so first paint and late morphs both match.
    this.#syncSizeSelectWidths();
    requestAnimationFrame(() => this.#syncSizeSelectWidths());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The originating click event from `on:click`.
   */
  onLineItemRemove(line, event) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      // Dstrezzed: no filled→empty view transition, and no optimistic template
      // swap (that disconnects the remove control mid-click and can close the
      // drawer). Hydration morph after cart/change updates the empty UI in place.
      if (this.#isDstrezzedDrawer()) {
        event?.stopPropagation?.();
        return;
      }

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles cart item size select changes.
   * @param {Event} event
   */
  async onSizeChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    this.#syncSizeSelectWidth(select);

    const group = select.closest('[data-cart-size-selector]');
    if (!(group instanceof HTMLElement) || !this.contains(group)) return;

    const lineKey = group.dataset.lineKey;
    const quantity = Number(group.dataset.quantity || '1');
    const currentVariantId = group.dataset.currentVariantId;
    const isSplit = group.dataset.split === 'true';
    const sellingPlan = group.dataset.sellingPlan;
    const variantMap = this.#parseSizeVariantMap(group);
    const properties = this.#parseCartItemProperties(group);

    if (!lineKey || !currentVariantId || Number.isNaN(quantity)) return;

    const sizeKey = isSplit ? this.#getSplitSizeKey(group, variantMap) : select.value;
    if (!sizeKey) {
      this.#restoreSizeSelects(group, variantMap, currentVariantId);
      return;
    }

    const nextVariant = variantMap[sizeKey];
    if (!nextVariant?.id) {
      this.#restoreSizeSelects(group, variantMap, currentVariantId);
      this.#showSizeChangeError(group, 'That size combination is unavailable.');
      return;
    }

    if (String(nextVariant.id) === String(currentVariantId)) return;

    if (nextVariant.available === false) {
      this.#restoreSizeSelects(group, variantMap, currentVariantId);
      this.#showSizeChangeError(group, 'That size is currently unavailable.');
      return;
    }

    const row = group.closest('tr');
    const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
    textComponent?.shimmer();

    try {
      await this.#changeLineVariant({
        lineKey,
        quantity,
        variantId: nextVariant.id,
        properties,
        sellingPlan,
      });
    } catch (error) {
      console.error(error);
      this.#restoreSizeSelects(group, variantMap, currentVariantId);
      this.#showSizeChangeError(group, error instanceof Error ? error.message : 'Could not update size.');
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal, cartTotalSaving } = this.refs;

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: this.#sectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    cartTotalSaving?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html',
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        // Hydration can recreate drawer internals; keep an already-open drawer open.
        this.#ensureDrawerStaysOpen();

        this.#updateCartQuantitySelectorButtonStates();
        this.#syncSizeSelectWidths();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#syncSizeSelectWidths();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * @returns {string[]}
   */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    }

    return Array.from(sectionsToUpdate);
  }

  /**
   * @param {HTMLElement} group
   * @returns {SizeVariantMap}
   */
  #parseSizeVariantMap(group) {
    const script = group.querySelector('[data-size-variant-map]');
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} group
   * @returns {Record<string, string>}
   */
  #parseCartItemProperties(group) {
    const script = group.querySelector('[data-cart-item-properties]');
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} group
   * @param {SizeVariantMap} variantMap
   * @returns {string | null}
   */
  #getSplitSizeKey(group, variantMap) {
    const waistSelect = group.querySelector('[data-size-axis="waist"]');
    const lengthSelect = group.querySelector('[data-size-axis="length"]');

    if (!(waistSelect instanceof HTMLSelectElement) || !(lengthSelect instanceof HTMLSelectElement)) {
      return null;
    }

    const waist = waistSelect.value.trim();
    const length = lengthSelect.value.trim();

    for (const key of Object.keys(variantMap)) {
      const parts = key.split('/').map((part) => part.trim());
      if (parts[0] === waist && parts[1] === length) {
        return key;
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} group
   * @param {SizeVariantMap} variantMap
   * @param {string} variantId
   */
  #restoreSizeSelects(group, variantMap, variantId) {
    const sizeKey = Object.keys(variantMap).find((key) => String(variantMap[key]?.id) === String(variantId));
    if (!sizeKey) return;

    if (group.dataset.split === 'true') {
      const [waist = '', length = ''] = sizeKey.split('/').map((part) => part.trim());
      const waistSelect = group.querySelector('[data-size-axis="waist"]');
      const lengthSelect = group.querySelector('[data-size-axis="length"]');

      if (waistSelect instanceof HTMLSelectElement) waistSelect.value = waist;
      if (lengthSelect instanceof HTMLSelectElement) lengthSelect.value = length;
      return;
    }

    const sizeSelect = group.querySelector('[data-size-select]');
    if (sizeSelect instanceof HTMLSelectElement) {
      sizeSelect.value = sizeKey;
    }
  }

  /**
   * @param {HTMLElement} group
   * @param {string} message
   */
  #showSizeChangeError(group, message) {
    const row = group.closest('tr');
    const errorContainer = row?.querySelector('[ref^="cartItemErrorContainer-"]');
    const errorText = row?.querySelector('[ref^="cartItemError-"]');

    if (!(errorContainer instanceof HTMLElement) || !(errorText instanceof HTMLElement)) return;

    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
  }

  /**
   * Swaps a cart line to a different variant while preserving quantity/properties.
   * @param {object} config
   * @param {string} config.lineKey
   * @param {number} config.quantity
   * @param {number} config.variantId
   * @param {Record<string, string>} config.properties
   * @param {string} [config.sellingPlan]
   */
  async #changeLineVariant(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change-variant:user-action');
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();
    this.refs.cartTotalSaving?.shimmer();

    const sections = this.#sectionsToUpdate().join(',');
    /** @type {Record<string, unknown>} */
    const addItem = {
      id: config.variantId,
      quantity: config.quantity,
    };

    if (Object.keys(config.properties).length > 0) {
      addItem.properties = config.properties;
    }

    if (config.sellingPlan) {
      addItem.selling_plan = Number(config.sellingPlan);
    }

    try {
      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [addItem],
            sections,
            sections_url: window.location.pathname,
          }),
        }),
      );
      const addResult = await addResponse.json();

      if (!addResponse.ok || addResult.status || addResult.message || addResult.description) {
        throw new Error(addResult.description || addResult.message || 'Could not add the selected size.');
      }

      const changeResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: config.lineKey,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        }),
      );
      const changeResult = await changeResponse.json();

      if (!changeResponse.ok || changeResult.errors) {
        throw new Error(
          typeof changeResult.errors === 'string' ? changeResult.errors : 'Could not update the selected size.',
        );
      }

      resetShimmer(this);

      const newSectionHTML = new DOMParser().parseFromString(changeResult.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(changeResult);

      this.dispatchEvent(
        new CartUpdateEvent(changeResult, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: changeResult.sections,
        }),
      );

      morphSection(this.sectionId, changeResult.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
      this.#updateCartQuantitySelectorButtonStates();
      this.#syncSizeSelectWidths();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  #syncSizeSelectWidths() {
    for (const select of this.querySelectorAll('.cart-items__size-select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      this.#syncSizeSelectWidth(select);
    }
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #syncSizeSelectWidth(select) {
    const selectedOption = select.options[select.selectedIndex];
    const selectedLabel = selectedOption?.textContent?.trim() || select.value || '';
    const widthEm = Math.max(selectedLabel.length * 0.65, 1);

    select.style.setProperty('--cart-size-ch', `${widthEm}em`);
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * @returns {boolean} Whether this drawer uses the Dstrezzed cart style.
   */
  #isDstrezzedDrawer() {
    return this.isDrawer && Boolean(this.closest('.cart-drawer__style--dstrezzed'));
  }

  /**
   * Re-open the cart drawer if morphing dropped the dialog `open` state.
   */
  #ensureDrawerStaysOpen() {
    if (!this.#isDstrezzedDrawer()) return;

    const dialog = this.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    const drawer = dialog.closest('cart-drawer-component');

    if (drawer && 'showDialog' in drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      return;
    }

    dialog.showModal();
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
